import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Lock, FileText, ArrowUpRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { KineticLines } from "@/components/KineticLines";

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-4 py-4 text-base text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";

const STATUS_BADGE = {
  draft: "bg-white/15 text-white/70",
  viewed: "bg-sky-400/20 text-sky-300",
  accepted: "bg-emerald-400/20 text-emerald-300",
};

export default function ReviewProposal() {
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
  }, []);

  const search = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/proposals/lookup", { address, phone });
      setResults(data);
      if (data.length === 0) setError("No proposal found for that address and phone number. Double-check both, or call us at (203) 307-5222.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="review-proposal-page" className="bg-background min-h-screen">
      <Header />
      <main className="pt-32 pb-24 px-5 md:px-10 max-w-3xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-primary text-xs font-bold tracking-[0.35em] uppercase mb-5"
        >
          Homeowners
        </motion.p>
        <KineticLines lines={["REVIEW YOUR", "PROPOSAL"]} className="font-display text-6xl md:text-8xl leading-[0.9] tracking-wide" />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 text-white/55 text-sm md:text-base leading-relaxed max-w-lg"
        >
          Lost your proposal link? No problem. Enter your house address and the phone number you gave us — your proposal pops right up, ready to review and approve.
        </motion.p>

        <motion.form
          onSubmit={search}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-10 border border-white/10 bg-card rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.25)] p-6 md:p-8 space-y-4"
          data-testid="review-form"
        >
          <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="House address — e.g. 14 Maple St, Stamford" data-testid="review-address-input" className={inputCls} required />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number — e.g. (203) 555-0123" data-testid="review-phone-input" className={inputCls} required />
          {error && <p className="text-red-400 text-sm" data-testid="review-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="review-submit-button" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-4 flex items-center justify-center gap-3 transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(193,214,46,0.35)] disabled:opacity-60">
            {loading ? "Searching..." : "Find My Proposal"} <Search size={16} aria-hidden="true" />
          </button>
        </motion.form>

        {results && results.length > 0 && (
          <div className="mt-8 space-y-3" data-testid="review-results">
            {results.map((p) => (
              <Link key={p.id} to={`/proposal/${p.id}`} data-testid={`review-result-${p.id}`} className="group border border-white/10 bg-card rounded-xl p-5 flex items-center gap-4 hover:border-primary/50 transition-colors duration-300">
                <div className="w-11 h-11 bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <FileText size={19} aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{p.title}</p>
                  <p className="text-xs text-white/50 mt-0.5">{p.client_name} · {new Date(p.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 shrink-0 ${STATUS_BADGE[p.status]}`}>{p.status === "accepted" ? "approved" : p.status}</span>
                <ArrowUpRight size={17} className="text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true" />
              </Link>
            ))}
          </div>
        )}

        <div className="mt-14 border-t border-white/10 pt-8 text-center">
          <Link to="/admin/login" data-testid="review-admin-login" aria-label="Admin login" className="inline-flex items-center p-2 text-white/20 hover:text-primary transition-colors duration-300">
            <Lock size={14} aria-hidden="true" />
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
